import { useEffect, useState } from "react";
import {
  Center, Container, Paper, Stack, Title, Text, Button, Group, Checkbox,
  Alert, Loader, Divider, ThemeIcon,
} from "@mantine/core";
import {
  IconShieldCheck, IconCircleCheck, IconCircleX, IconAlertTriangle, IconClockOff,
} from "@tabler/icons-react";
import { getPublicConsent, getConsentPreview, respondPublicConsent, withdrawPublicConsent } from "../api";

// The microsite speaks the subject's language (the request locale), not the staff
// user's. Chrome strings live here; the body text itself is the tenant's content.
const STRINGS = {
  de: {
    review: "Bitte prüfen Sie den folgenden Text und teilen Sie uns Ihre Entscheidung mit.",
    controller: "Verantwortlich",
    retention: "Speicherdauer",
    ack: "Ich habe den Text gelesen und verstanden.",
    approve: "Zustimmen",
    decline: "Ablehnen",
    approvedTitle: "Vielen Dank — Ihre Einwilligung wurde aufgezeichnet.",
    declinedTitle: "Ihre Ablehnung wurde aufgezeichnet. Vielen Dank.",
    withdraw: "Einwilligung widerrufen",
    withdrawnTitle: "Ihre Einwilligung wurde widerrufen.",
    unavailable: "Dieser Link ist nicht mehr gültig.",
    expired: "Dieser Link ist abgelaufen. Bitte wenden Sie sich an Ihren Ansprechpartner.",
    notFound: "Dieser Link wurde nicht gefunden oder ist ungültig.",
    recordedAt: "Aufgezeichnet am",
    previewBanner: "Vorschau — so sieht es für Ihre Kontakte aus. Ihre Antwort wird nicht gespeichert.",
  },
  en: {
    review: "Please review the following and let us know your decision.",
    controller: "Data controller",
    retention: "Retention period",
    ack: "I have read and understood the text.",
    approve: "Approve",
    decline: "Decline",
    approvedTitle: "Thank you — your consent has been recorded.",
    declinedTitle: "Your decision to decline has been recorded. Thank you.",
    withdraw: "Withdraw consent",
    withdrawnTitle: "Your consent has been withdrawn.",
    unavailable: "This link is no longer valid.",
    expired: "This link has expired. Please contact your point of contact.",
    notFound: "This link could not be found or is invalid.",
    recordedAt: "Recorded on",
    previewBanner: "Preview — this is what your contacts see. Your response is not recorded.",
  },
};

export default function ConsentReview({ token, preview = false }) {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (preview ? getConsentPreview(token) : getPublicConsent(token))
      .then((d) => { if (active) { setData(d); setPhase("ready"); } })
      .catch(() => { if (active) setPhase("error"); });
    return () => { active = false; };
  }, [token, preview]);

  const s = STRINGS[data?.locale] ?? STRINGS.en;
  const accent = data?.accentColor || "#2C5F4A"; // brand forest

  const respond = (approve) => {
    // Preview: never hit the server — record nothing, just advance the UI to the receipt.
    if (preview) {
      setData({
        ...data,
        status: approve ? "approved" : "declined",
        canRespond: false,
        canWithdraw: approve,
        respondedAt: new Date().toISOString(),
      });
      return;
    }
    setSubmitting(true);
    respondPublicConsent(token, approve)
      .then((d) => d && setData(d))
      .catch(() => {})
      .finally(() => setSubmitting(false));
  };

  const withdraw = () => {
    if (preview) {
      setData({ ...data, withdrawn: true, canWithdraw: false });
      return;
    }
    setSubmitting(true);
    withdrawPublicConsent(token)
      .then((d) => d && setData(d))
      .catch(() => {})
      .finally(() => setSubmitting(false));
  };

  if (phase === "loading") {
    return <Center mih="100vh"><Loader color={accent} /></Center>;
  }

  if (phase === "error") {
    return (
      <Center mih="100vh" p="md">
        <Paper withBorder p="xl" maw={520} w="100%" radius="md">
          <Alert color="gray" icon={<IconAlertTriangle size={18} />}>
            {STRINGS.en.notFound}
          </Alert>
        </Paper>
      </Center>
    );
  }

  const decided = data.status === "approved" || data.status === "declined";

  return (
    <Center mih="100vh" p="md" style={{ background: "#FAF7F1" }}>
      <Container size="sm" w="100%" px={0}>
        <Paper withBorder p="xl" radius="md" shadow="xs" style={{ background: "#FFFDF8" }}>
          <Stack gap="md">
            {preview && (
              <Alert color="blue" variant="light" icon={<IconAlertTriangle size={16} />}>
                {s.previewBanner}
              </Alert>
            )}
            <Group gap="sm" align="center">
              <ThemeIcon variant="light" size="lg" radius="xl" color={accent}>
                <IconShieldCheck size={20} />
              </ThemeIcon>
              {data.controllerIdentity && (
                <Text size="sm" c="dimmed" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {s.controller}: {data.controllerIdentity}
                </Text>
              )}
            </Group>

            <Title order={3}>{data.subject}</Title>

            <Text size="sm" c="dimmed">{s.review}</Text>

            <Divider />

            <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{data.body}</Text>

            <Text size="sm" c="dimmed">
              {s.retention}: <b>{data.validityLabel}</b>
            </Text>

            <Divider />

            {/* Pending — collect the decision */}
            {data.canRespond && (
              <Stack gap="sm">
                <Checkbox
                  checked={ack}
                  onChange={(e) => setAck(e.currentTarget.checked)}
                  label={s.ack}
                />
                <Group justify="flex-end">
                  <Button
                    variant="default"
                    leftSection={<IconCircleX size={16} />}
                    disabled={submitting}
                    onClick={() => respond(false)}
                  >
                    {s.decline}
                  </Button>
                  <Button
                    color={accent}
                    leftSection={<IconCircleCheck size={16} />}
                    disabled={!ack || submitting}
                    loading={submitting}
                    onClick={() => respond(true)}
                  >
                    {s.approve}
                  </Button>
                </Group>
              </Stack>
            )}

            {/* Decided — approved */}
            {data.status === "approved" && !data.withdrawn && (
              <Stack gap="sm">
                <Alert color="teal" icon={<IconCircleCheck size={18} />}>
                  {s.approvedTitle}
                  {data.respondedAt && (
                    <Text size="xs" c="dimmed" mt={4}>
                      {s.recordedAt} {new Date(data.respondedAt).toLocaleString()}
                    </Text>
                  )}
                </Alert>
                {data.canWithdraw && (
                  <Group justify="flex-end">
                    <Button variant="subtle" color="red" disabled={submitting} onClick={withdraw}>
                      {s.withdraw}
                    </Button>
                  </Group>
                )}
              </Stack>
            )}

            {/* Decided — withdrawn */}
            {data.withdrawn && (
              <Alert color="gray" icon={<IconCircleX size={18} />}>{s.withdrawnTitle}</Alert>
            )}

            {/* Decided — declined */}
            {data.status === "declined" && (
              <Alert color="gray" icon={<IconCircleX size={18} />}>{s.declinedTitle}</Alert>
            )}

            {/* Lapsed / cancelled */}
            {(data.status === "expired" || data.linkExpired) && (
              <Alert color="orange" icon={<IconClockOff size={18} />}>{s.expired}</Alert>
            )}
            {data.status === "revoked" && (
              <Alert color="gray" icon={<IconAlertTriangle size={18} />}>{s.unavailable}</Alert>
            )}

            {data.footer && (
              <Text size="xs" c="dimmed" ta="center" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {data.footer}
              </Text>
            )}
          </Stack>
        </Paper>
      </Container>
    </Center>
  );
}
